import React, { useState, useRef, useEffect } from "react"
import { useParams } from "react-router-dom"
import { DragDropProvider } from "@dnd-kit/react"
import { move } from "@dnd-kit/helpers"
import { useToast } from "~/utils/Toast"

import LoadingWrapper from "~/library/LoadingWrapper"
import DroppableStatusSection from "./DroppableStatusSection"
import NewTask from "~/views/Sponsorship/NewTask"
import useFetchResource from "~/hooks/useFetchResource"
import getAppConfig from "~/utils/getAppConfig"
import { apiSponsorshipTasks } from "~/routes"
import { TasksResponse, Task } from "~/types/sponsorship/Task"
import { Plus } from "~/library/UIkit/Icons"

const TasksTab = () => {
  const { venueSlug } = getAppConfig()
  const { id: sponsorshipId } = useParams<{ id: string }>()
  const toast = useToast()

  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    not_started: false,
    upcoming: false,
    in_progress: false,
    completed: false,
  })

  const [tasks, setTasks] = useState<{ [key: string]: Task[] }>({})
  const previousTasks = useRef<{ [key: string]: Task[] }>({})

  const [showNewTaskModal, setShowNewTaskModal] = useState(false)

  const statusKeys = ["not_started", "upcoming", "in_progress", "completed"]
  const statusLabels = {
    not_started: "Not Started",
    upcoming: "Upcoming",
    in_progress: "In Progress",
    completed: "Completed",
  }

  const endpoint = apiSponsorshipTasks.index.path({
    venueSlug,
    sponsorshipId: sponsorshipId!,
  })

  const {
    data: tasksData,
    isLoading,
    refetch,
  } = useFetchResource<TasksResponse, "sponsorshipTasks">(endpoint, "sponsorshipTasks", {})

  const normalizeTasks = () => {
    const normalized = statusKeys.reduce((acc, status) => {
      acc[status] = tasksData[status] ?? []
      return acc
    }, {} as { [key: string]: Task[] })

    setTasks(normalized)

    prepareExpandedSections(normalized)
  }

  const prepareExpandedSections = (tasks: { [key: string]: Task[] }) => {
    setExpandedSections((prev) => ({
      ...prev,
      not_started: tasks.not_started.length > 0,
      upcoming: tasks.upcoming.length > 0,
      in_progress: tasks.in_progress.length > 0,
      completed: tasks.completed.length > 0,
    }))
  }

  useEffect(() => {
    if (tasksData && !isLoading) {
      normalizeTasks()
    }
  }, [tasksData, isLoading])

  const toggleSection = (status: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [status]: !prev[status],
    }))
  }

  const handleDragStart = () => {
    previousTasks.current = tasks
  }

  const handleDragOver = (event: any) => {
    const { source } = event.operation

    if (source?.type !== "task") return

    setTasks((currentTasks) => move(currentTasks, event))
  }

  const handleDragEnd = (event: any) => {
    const { source, target } = event.operation

    if (!target) return

    if (event.canceled) {
      if (source.type === "task") {
        setTasks(previousTasks.current)
      }
      return
    }

    if (target.type !== "task") return

    const { sortable, data } = target
    const newPosition = sortable.index + 1

    if (data.initialIndex === newPosition && data.initialStatus === sortable.group) return

    setTasks((currentTasks) => {
      const updatedTasks = { ...currentTasks }

      for (const status of Object.keys(updatedTasks)) {
        const taskIndex = updatedTasks[status].findIndex((task) => task.id === target.id)
        if (taskIndex !== -1) {
          updatedTasks[status][taskIndex] = {
            ...updatedTasks[status][taskIndex],
            status: sortable.group,
            position: newPosition,
          }
          break
        }
      }

      return updatedTasks
    })

    const requestData = {
      venue_slug: venueSlug,
      sponsorshipId: sponsorshipId,
      id: target.id,
      sponsorshipTask: {
        status: sortable.group,
        position: newPosition,
      },
    }

    const req = apiSponsorshipTasks.update({ data: requestData })
    req.then().catch((res) => {
      console.log("Update task position error:", res.body?.errors?.join(", "))
    })
  }

  const deleteTask = (taskId: string) => {
    const req = apiSponsorshipTasks.destroy({ data: { venueSlug, sponsorshipId, id: taskId } })
    req
      .then(() => {
        setTasks((currentTasks) => {
          const updatedTasks = { ...currentTasks }
          Object.keys(updatedTasks).forEach((status) => {
            updatedTasks[status] = updatedTasks[status].filter((task) => task.id !== taskId)
          })
          return updatedTasks
        })
        toast("Task deleted successfully", "success")
      })
      .catch((res) => {
        toast(
          `Error deleting task. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
      })
  }

  const handleTaskSuccess = () => {
    refetch()
  }

  return (
    <div className="row">
      <div className="col-12">
        <LoadingWrapper isLoading={isLoading}>
          <div className="d-flex justify-content-end mb-2">
            <button
              className="btn-catch-primary btn btn-primary"
              onClick={() => setShowNewTaskModal(true)}
            >
              <Plus color={"#ffffff"} />
            </button>
          </div>

          {Object.keys(tasks).length > 0 ? (
            <DragDropProvider
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <div>
                {statusKeys.map((status) => {
                  const statusTasks = tasks[status] || []
                  const statusLabel = statusLabels[status as keyof typeof statusLabels]

                  return (
                    <DroppableStatusSection
                      key={status}
                      status={status}
                      statusLabel={statusLabel}
                      tasks={statusTasks}
                      isExpanded={expandedSections[status]}
                      onToggle={() => toggleSection(status)}
                      onDeleteTask={deleteTask}
                      onTaskUpdated={handleTaskSuccess}
                    />
                  )
                })}

                {Object.values(tasks).every((statusTasks) => statusTasks.length === 0) && (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">No tasks have been created yet</p>
                  </div>
                )}
              </div>
            </DragDropProvider>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No tasks have been created yet</p>
            </div>
          )}
        </LoadingWrapper>

        {showNewTaskModal && (
          <NewTask
            isOpen={showNewTaskModal}
            onClose={() => setShowNewTaskModal(false)}
            onSuccess={handleTaskSuccess}
          />
        )}
      </div>
    </div>
  )
}

export default TasksTab
